// src/debug-flag.ts — runtime debug flag for UAT / remote investigation.
//
// Usage: import { isDebugEnabled } from './debug-flag';
//
// In development (import.meta.env.DEV === true) the function always returns
// true, so all DEV-gated code paths run exactly as before.
//
// In production builds the debug flag is controlled at runtime via the URL
// query string:
//   ?debug=1  — enables the flag and persists to localStorage['hunter:debug']
//   ?debug=0  — disables the flag and removes the key
//   (absent)  — reads from localStorage['hunter:debug']
//
// The resolved value is memoized after the first call so it is stable for the
// entire session (URL is only read once; changing the param mid-session
// requires a reload, which is the expected flow).
//
// All window / localStorage / URL access is wrapped in try/catch so the
// module is safe in:
//   - Safari private-browsing mode (localStorage.setItem throws)
//   - Vitest / jsdom environments without a real window object
//   - SSR / worker contexts
//
// Architecture note: the `hunter:*` localStorage namespace is the reserved
// debug/settings namespace per the project CLAUDE.md stack spec. This key
// is the only debug-related one written here; nothing else touches it.

let cached: boolean | null = null;

/**
 * Returns true when debug mode is active for this session.
 *
 * Resolution order (first truthy match wins):
 *   1. import.meta.env.DEV — always true in `pnpm dev` / Vitest
 *   2. ?debug=1 in the URL (writes to localStorage; ?debug=0 clears it)
 *   3. localStorage['hunter:debug'] === '1'
 *   4. false (production build, no flag set)
 *
 * Memoized: value is computed once on first call and cached for the session.
 */
export function isDebugEnabled(): boolean {
  if (cached !== null) return cached;

  // Fast path: in dev / Vitest environments DEV is literally `true`.
  // This must remain as the literal `import.meta.env.DEV` string so Vite
  // does NOT tree-shake the entire debug pipeline in production — only this
  // short-circuit path is compile-time folded.
  if (import.meta.env.DEV) {
    cached = true;
    return true;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('debug')) {
      const val = params.get('debug');
      if (val === '1') {
        localStorage.setItem('hunter:debug', '1');
        cached = true;
        return true;
      } else {
        // ?debug=0 or any other value clears the flag.
        localStorage.removeItem('hunter:debug');
        cached = false;
        return false;
      }
    }
    // No ?debug param — fall through to localStorage check.
    cached = localStorage.getItem('hunter:debug') === '1';
  } catch {
    // localStorage may throw in Safari private mode or restricted environments.
    // Fall back to false (no debug overlay for mysterious environments).
    cached = false;
  }

  return cached;
}
